import { useCallback, useEffect, useState } from 'react';
import { getFilterPreferenceSelection } from '../Preferences/filterPreferencesConfig';


export const useSavedPreferences = (storageKey, defaultFields) => {
  const [preferenceSelection, setPreferenceSelection] = useState(() =>
    getFilterPreferenceSelection(storageKey, defaultFields)
  );

  useEffect(() => {
    const syncPreferenceSelection = () => {
      setPreferenceSelection((current) => {
        const next = getFilterPreferenceSelection(storageKey, defaultFields);
        if (
          current.enabled === next.enabled &&
          current.selectedFields.length === next.selectedFields.length &&
          current.selectedFields.every((f, i) => f === next.selectedFields[i])
        ) {
          return current;
        }
        return next;
      });
    };

    const handleStorageChange = (event) => {
      if (!event?.key || event.key === storageKey) {
        syncPreferenceSelection();
      }
    };

    syncPreferenceSelection();
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', syncPreferenceSelection);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', syncPreferenceSelection);
    };
  }, [storageKey, defaultFields]);

  const loadSavedPreferences = useCallback(() => {
    const result = { ...defaultFields };

    if (!preferenceSelection.enabled || preferenceSelection.selectedFields.length === 0) {
      return result;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        preferenceSelection.selectedFields.forEach((key) => {
          result[key] = parsed[key] || [];
        });
        return result;
      }
    } catch (error) {
      console.error('Error loading saved preferences:', error);
    }
    return result;
  }, [storageKey, defaultFields, preferenceSelection.enabled, preferenceSelection.selectedFields]);

  const savePreferences = useCallback((preferences) => {
    try {
      if (!preferenceSelection.enabled || preferenceSelection.selectedFields.length === 0) {
        localStorage.removeItem(storageKey);
        window.dispatchEvent(new Event('localStorageChange'));
        return true;
      }

      const prefsToSave = {};
      preferenceSelection.selectedFields.forEach((key) => {
        prefsToSave[key] = preferences[key] || [];
      });

      const hasData = Object.values(prefsToSave).some(
        (value) => Array.isArray(value) && value.length > 0
      );

      if (hasData) {
        localStorage.setItem(storageKey, JSON.stringify(prefsToSave));
      } else {
        localStorage.removeItem(storageKey);
      }

      window.dispatchEvent(new Event('localStorageChange'));
      return true;
    } catch (error) {
      console.error('Error saving preferences:', error);
      return false;
    }
  }, [storageKey, preferenceSelection.enabled, preferenceSelection.selectedFields]);

  const unsavePreferences = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      window.dispatchEvent(new Event('localStorageChange'));
      return true;
    } catch (error) {
      console.error('Error unsaving preferences:', error);
      return false;
    }
  }, [storageKey]);

  const hasSavedPreferences = useCallback(() => {
    if (!preferenceSelection.enabled || preferenceSelection.selectedFields.length === 0) {
      return false;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return preferenceSelection.selectedFields.some(
          (field) => Array.isArray(parsed[field]) && parsed[field].length > 0
        );
      }
      return false;
    } catch (error) {
      return false;
    }
  }, [storageKey, preferenceSelection.enabled, preferenceSelection.selectedFields]);

  return {
    loadSavedPreferences,
    savePreferences,
    unsavePreferences,
    hasSavedPreferences,
    enabledFields: preferenceSelection.selectedFields,
    isPreferenceEnabled: preferenceSelection.enabled,
  };
};

